import type { Option } from '@byecode/ui'
import { Box, Button, IconFont, Popover, SelectDropdown } from '@byecode/ui'
import type { AggregateResultField } from '@lighthouse/shared'
import { FieldTypeTag, getFieldIcon } from '@lighthouse/shared'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

interface SelectFieldProps {
    value?: string[]
    fieldList: AggregateResultField[]
    onSelect: (val: string) => void
}

export const SelectField: React.FC<SelectFieldProps> = ({ value, onSelect, fieldList }) => {
    const [opened, setOpened] = useState(false)

    const options = useMemo(() => {
        return fieldList.reduce<Option[]>((prev, field) => {
            if (value?.includes(field.id)) {
                return prev
            }
            prev.push({
                label: field.name,
                value: field.id,
                icon: getFieldIcon(field.id, field.type, field.innerType),
                extra: <FieldTypeTag type={field.type} innerType={field.innerType} />
            })
            return prev
        }, [])
    }, [fieldList, value])

    const handleOpen = useCallback(() => {
        setOpened(true)
    }, [])

    const handleClose = useCallback(() => {
        setOpened(false)
    }, [])

    const handleSelect = useCallback(
        (val: string) => {
            onSelect(val)
            handleClose()
        },
        [onSelect, handleClose]
    )

    return (
        <Box mt={22}>
            <Popover opened={opened} withinPortal onChange={setOpened} width={220} position="bottom-start">
                <Popover.Target>
                    <Button
                        disabled={options.length === 0}
                        radius={100}
                        onClick={handleOpen}
                        icon={<IconFont type="Add" fill="var(--color-gray-500)" size={16} />}
                    >
                        添加
                    </Button>
                </Popover.Target>
                <Popover.Dropdown>
                    <SelectDropdown searchable onSelect={handleSelect} options={options} />
                </Popover.Dropdown>
            </Popover>
        </Box>
    )
}
